import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import RosterSlotGrid from "../components/community/RosterSlotGrid";
import { useGetCommunityRosterQuery } from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx, brandOutlinedButtonSx } from "../utils/brandAssets";
import {
  communityBuildPath,
  communityProfilePath,
  publicRosterPath,
  rosterOwnerLabel,
} from "../utils/community";

const CommunityRoster = ({ publicView = false }) => {
  const { idOrSlug } = useParams();
  const { sport } = useSportWorkspace();
  const { data: roster, isLoading, error } = useGetCommunityRosterQuery(idOrSlug, {
    skip: !idOrSlug,
  });
  const { trackCustomEvent } = useAnalytics();
  const [notice, setNotice] = React.useState("");

  const copyLink = async () => {
    const path = publicRosterPath(roster);
    if (!path) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      setNotice("Link copied");
      trackCustomEvent("community_object_shared", {
        sport: roster.sport,
        type: "roster",
      });
    } catch (_err) {
      setNotice("Could not copy link");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !roster) {
    return <Alert severity="info">This roster isn’t public yet.</Alert>;
  }

  return (
    <Box sx={{ py: publicView ? 0 : { xs: 1, sm: 2 }, maxWidth: 1280, mx: "auto" }}>
      <SeoHead
        title={`${roster.title} — Stats.Fan Community`}
        description={`A ${roster.sport.toUpperCase()} roster by ${rosterOwnerLabel(
          roster
        )} on Stats.Fan.`}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.16em" }}
          >
            {roster.sport.toUpperCase()} roster
          </Typography>
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
            {roster.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {roster.owner?.username || roster.owner?.handle ? (
              <Box
                component={RouterLink}
                to={communityProfilePath(
                  roster.sport,
                  roster.owner.handle || roster.owner.username
                )}
                sx={{ color: "inherit" }}
              >
                {rosterOwnerLabel(roster)}
              </Box>
            ) : (
              rosterOwnerLabel(roster)
            )}
            {roster.inspiredByLabel ? ` · Inspired by ${roster.inspiredByLabel}` : ""}
          </Typography>
          {roster.description && (
            <Typography variant="body1" sx={{ mt: 2, maxWidth: 640 }}>
              {roster.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            component={RouterLink}
            to={communityBuildPath(roster.sport, { versus: roster.id })}
            variant="contained"
            sx={brandFilledButtonSx}
          >
            Challenge this roster
          </Button>
          <Button
            component={RouterLink}
            to={communityBuildPath(roster.sport, { remix: roster.id })}
            variant="outlined"
            sx={brandOutlinedButtonSx}
          >
            Make my version
          </Button>
          <Button variant="outlined" onClick={copyLink}>
            Share
          </Button>
        </Box>
      </Box>

      <RosterSlotGrid roster={roster} sport={roster.sport || sport} />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default CommunityRoster;
